import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';
import i18n from '@/shared/i18n/i18n';

import {
  createPage,
  deletePage,
  getPage,
  listBlocks,
  listPages,
  publishPage,
  replaceBlocks,
  unpublishPage,
  updatePage,
  type ContentPagesFilters,
} from './api/contentApi';
import type {
  ContentBlock,
  ContentPage,
  CreatePageRequest,
  PageSummary,
  ReplaceBlocksRequest,
  UpdatePageRequest,
} from './model/types';

type Err = NormalizedApiError;

const CONTENT_QUERY_KEY = ['content'] as const;

export const contentQueryKeys = {
  all: CONTENT_QUERY_KEY,
  pages: (filters: ContentPagesFilters) => [...CONTENT_QUERY_KEY, 'pages', filters] as const,
  page: (slug: string, locale?: string) => [...CONTENT_QUERY_KEY, 'page', slug, locale ?? i18n.language] as const,
  blocks: (slug: string, locale?: string) => [...CONTENT_QUERY_KEY, 'blocks', slug, locale ?? i18n.language] as const,
};

export function useContentPages(filters: ContentPagesFilters, enabled = true): UseQueryResult<PageSummary[], Err> {
  return useQuery<PageSummary[], Err>({
    queryKey: contentQueryKeys.pages(filters),
    queryFn: () => listPages(filters),
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useContentPage(slug: string | undefined, locale?: string, enabled = true): UseQueryResult<ContentPage, Err> {
  return useQuery<ContentPage, Err>({
    queryKey: slug ? contentQueryKeys.page(slug, locale) : [...CONTENT_QUERY_KEY, 'page', 'empty'] as const,
    queryFn: () => getPage(slug ?? '', locale),
    enabled: enabled && Boolean(slug),
    staleTime: 30_000,
    retry: 1,
  });
}

export function useCreateContentPage() {
  const queryClient = useQueryClient();
  return useMutation<ContentPage, Err, CreatePageRequest>({
    mutationFn: (body) => createPage(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CONTENT_QUERY_KEY });
    },
  });
}

export function useUpdateContentPage() {
  const queryClient = useQueryClient();
  return useMutation<ContentPage, Err, { slug: string; locale?: string; body: UpdatePageRequest }>({
    mutationFn: (input) => updatePage(input.slug, input.body, input.locale),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CONTENT_QUERY_KEY });
    },
  });
}

export function useDeleteContentPage() {
  const queryClient = useQueryClient();
  return useMutation<void, Err, { slug: string; locale?: string }>({
    mutationFn: (input) => deletePage(input.slug, input.locale),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CONTENT_QUERY_KEY });
    },
  });
}

export function usePublishContentPage() {
  const queryClient = useQueryClient();
  return useMutation<ContentPage, Err, { slug: string; locale?: string }>({
    mutationFn: (input) => publishPage(input.slug, input.locale),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CONTENT_QUERY_KEY });
    },
  });
}

export function useUnpublishContentPage() {
  const queryClient = useQueryClient();
  return useMutation<ContentPage, Err, { slug: string; locale?: string }>({
    mutationFn: (input) => unpublishPage(input.slug, input.locale),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CONTENT_QUERY_KEY });
    },
  });
}

export function useListContentBlocks(slug: string | undefined, locale?: string, enabled = true): UseQueryResult<ContentBlock[], Err> {
  return useQuery<ContentBlock[], Err>({
    queryKey: slug ? contentQueryKeys.blocks(slug, locale) : [...CONTENT_QUERY_KEY, 'blocks', 'empty'] as const,
    queryFn: () => listBlocks(slug ?? '', locale),
    enabled: enabled && Boolean(slug),
    staleTime: 30_000,
    retry: 1,
  });
}

export function useReplaceContentBlocks() {
  const queryClient = useQueryClient();
  return useMutation<ContentBlock[], Err, { slug: string; locale?: string; body: ReplaceBlocksRequest }>({
    mutationFn: (input) => replaceBlocks(input.slug, input.body, input.locale),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CONTENT_QUERY_KEY });
    },
  });
}
